/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.uploadModule')
  .controller('BulbUploadController', [
    '$interval',
    '$q',
    '$translate',
    'AeroDialogService',
    'AssetUploadEventService',
    'AssetUploadHttpService',
    'BulbAssetLibraryService',
    'BulbBusyService',
    'BulbErrorService',
    'BulbUploadService',
    'UppyDashboardService',
    'CONFIG',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    BulbUploadController]);

/**
 * A controller for uploading assets using Plupload.
 */
function BulbUploadController(
  $interval,
  $q,
  $translate,
  AeroDialogService,
  AssetUploadEventService,
  AssetUploadHttpService,
  BulbAssetLibraryService,
  BulbBusyService,
  BulbErrorService,
  BulbUploadService,
  UppyDashboardService,
  CONFIG,
) {
  const that = this;
  /**
   * The asset id for the next upload.
   * @type {null}
   */
  this.assetId = null;

  /**
   * If true, show the plupload control,
   * else show the initiation button.
   * @type {boolean}
   */
  this.isActive = false;

  /**
   * This will house the function that needs to be called to disable the busy indicator on uploads.
   * @type {function}
   */
  this.stopBusyIndicatorCallback = null;

  /**
   * The list of files to be uploaded, populated by plupload.
   * @type {Array}
   */
  this.files = [];

  /**
   * The name of the file currently being uploaded.
   * Used to show the file name in the progress bar.
   * @type (string)
   */
  this.fileName = null;

  /**
   * The error message.
   * @type {string}
   */
  this.errorMessage = '';

  /**
   * True if invalid but tooltip is hidden.
   * Used to control progress bar cursor.
   * @type {boolean}
   */
  this.isTooltipHidden = false;

  /**
   * Parameters to be passed in the POST to the server.
   */
  this.multiParams = {
    name: null,
    assetId: null,
    assetDefinitionsKey: this.assetDefinitionsKey,
  };

  /**
   * Set url that Plupload posts to, based on assetType.
   * @type {string}
   */
  this.url = `/b/${this.assetType}/upload`;

  this.onboardingIconStyle = {
    backgroundImage: 'url("resources/images/onboarding_task_icon_background.png")',
  };

  this.openUppyModal = function openUppyModal(uploadState) {
    UppyDashboardService.open({
      assetLibrary: true,
      multiSelect: false,
      allowedFileTypes: ['image/*', '.heic', '.heif'], // This is the UppyAllowedFileTypes.IMAGE type.
      uploadState,
    });

    const assetEvents = AssetUploadEventService.uppyEventEmitter.subscribe((event) => {
      if (event.status === 'PROCESSING_SUCCESS' && event.uploadState === uploadState) {
        BulbBusyService.showBusy();
        const uploadSub = AssetUploadHttpService
          .fetchAssetLibraryAsset(event.response.entry.id).subscribe((asset) => {
            if (asset.entry.status === 'COMPLETE') {
              that.finishProcess(asset.entry);
            } else if (asset.entry.status === 'ERROR') {
              AeroDialogService.showDialog({
                icon: 'fa fa-exclamation-triangle',
                iconClass: 'aero-alert',
                title: $translate.instant('modal_oops_title'),
                detail: $translate.instant('modal_image_heic_warning'),
                primaryLabel: $translate.instant('button_ok'),
                closeOnPrimary: true,
                showSecondary: false,
              });
            }
            BulbBusyService.hideBusy();
            uploadSub.unsubscribe();
          });
        assetEvents.unsubscribe();
      }
    });
  };

  this.finishProcess = function finishProcess(entry) {
    BulbAssetLibraryService.dialog.dismissAllModals();
    return that.onFileUploaded(entry).then(() => {
      that.isActive = false;
    });
  };

  this.addFileHandler = function addFileHandler(event, data) {
    that.stopBusyIndicatorCallback = BulbBusyService.showBusy();
    that.fileName = data.files[0].name;
    that.isActive = true;
  };

  this.submitFileHandler = function submitFileHandler(/* event, data */) {
    // Nice dude! Nothing here
  };

  this.doneFileHandler = function doneFileHandler(event, data) {
    that.waitEntryReady(data.entry.id).then(() => {
      // Do nothing on success...
    }, (reason) => {
      that.errorMessage = BulbErrorService.error(`onFileUploaded error: ${reason}`);
    }).finally(() => {
      that.stopBusyIndicatorCallback();
    });
  };

  /**
   * This function is used to handle error when file is unable to process.
   * @param error Error object thrown from the server.
   */
  this.errorFileHandler = function errorFileHandler(/* error */) {
    that.isActive = false;
    that.stopBusyIndicatorCallback();
  };

  this.waitEntryReady = function waitEntryReady(entryId) {
    const deferred = $q.defer();
    const checkUploadingStatusInterval = 1000; // ms
    // check uploading & server processing status
    const timer = $interval(() => {
      BulbAssetLibraryService.api.getEntry(entryId).then((res) => {
        const { entry } = res;
        if (entry.status === 'COMPLETE') {
          that.finishProcess(entry);
          $interval.cancel(timer);
          deferred.resolve();
        } else if (entry.status === 'ERROR') {
          $interval.cancel(timer);
          deferred.reject();
        }
      }, () => {
        // entry was deleted.
        $interval.cancel(timer);
        deferred.reject();
      });
    }, checkUploadingStatusInterval);

    that.isActive = false;

    return deferred.promise;
  };

  /**
   * Completion callback for uploading a file.
   * Calls the onFileUploaded function, if any.
   * The restReply returned by the server is passed to the onFileUploaded function.
   * The onFileUploaded function should return a promise.
   * When the promise is resolved, clear the current upload.
   * @param response the restReply returned by the server
   */
  this.fileUploaded = function fileUploaded(response) {
    that.uploadComplete = true;
    // This should be set automatically by Plupload, but for some reason that doesn't work.
    // Race condition?
    const restReply = jQuery.parseJSON(response.response);
    if (restReply.success) {
      if (that.onFileUploaded) {
        BulbBusyService.showBusyDuring(
          // invoke onFileUploaded handler
          $q.when(that.onFileUploaded({ response: restReply })).then(() => {
            that.isActive = false;
            that.clearUpload();
          }, (reason) => {
            that.errorMessage = BulbErrorService.error(`onFileUploaded error: ${reason}`);
            that.clearUpload();
          }),
        );
      }
    } else if (restReply.message) {
      that.errorMessage = restReply.message;
    } else {
      // add default message
      that.errorMessage = 'Cannot upload';
    }
  };

  // Clear out the current upload.
  this.clearUpload = function clearUpload(file) {
    // Mark the upload ended.
    if (file) {
      BulbUploadService.endUpload(file);
    }
    that.errorMessage = null;
    that.uploadComplete = false;
    that.fileName = null;
    that.multiParams.name = null;
    that.multiParams.assetId = null;
    // Get the next asset id.
    BulbUploadService.getNewAssetId().then((assetId) => {
      that.assetId = assetId;
    }, (reason) => {
      that.errorMessage = BulbErrorService.error(`Cannot get new asset Id: ${reason}`);
    });
  };
}
